<template>
  <header
    class="header"
    :class="{
      'header-scrolled': isScrolled,
      'menu-open': isMenuOpen,
    }"
  >
    <!-- Mobile Menu Overlay -->
    <div
      class="mobile-overlay"
      :class="{ active: isMenuOpen }"
      @click="closeMenu"
    ></div>

    <div class="header-container">
      <div class="header-inner">
        <!-- Logo -->
        <router-link to="/" class="logo">
          <span class="logo-text">Vision Lab</span>
          <span class="logo-dot"></span>
        </router-link>

        <!-- Desktop Navigation -->
        <nav class="nav-menu desktop-nav">
          <ul class="nav-list">
            <li
              v-for="(item, index) in menuItems"
              :key="index"
              class="nav-item"
            >
              <router-link
                :to="item.to"
                class="nav-link"
                :class="{
                  active: isActiveLink(item),
                }"
              >
                {{ item.text }}
                <span class="nav-indicator"></span>
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- CTA Button -->
        <div class="cta-wrapper desktop-cta">
          <router-link to="/#contact" class="cta-button">
            Get Started
            <svg
              class="cta-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M5 12h14m-6-6l6 6-6 6"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </router-link>
        </div>

        <!-- Mobile Menu Button -->
        <button
          class="mobile-toggle"
          :class="{ active: isMenuOpen }"
          @click="toggleMenu"
          aria-label="Toggle Menu"
        >
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <nav class="mobile-nav" :class="{ active: isMenuOpen }">
      <div class="mobile-nav-content">
        <!-- Mobile Menu Items -->
        <ul class="mobile-nav-list">
          <li
            v-for="(item, index) in menuItems"
            :key="index"
            class="mobile-nav-item"
            :style="{ '--delay': (index + 1) * 0.08 + 's' }"
          >
            <router-link
              :to="item.to"
              class="mobile-nav-link"
              :class="{
                active: isActiveLink(item),
              }"
              @click="closeMenu"
            >
              {{ item.text }}
            </router-link>
          </li>
        </ul>

        <!-- Mobile CTA -->
        <div class="mobile-cta" :style="{ '--delay': '0.5s' }">
          <router-link
            to="/#contact"
            class="mobile-cta-button"
            @click="closeMenu"
          >
            Get Started
            <svg
              class="mobile-cta-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M5 12h14m-6-6l6 6-6 6"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </router-link>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
export default {
  name: "ModernHeader",
  data() {
    return {
      isScrolled: false,
      isMenuOpen: false,
      menuItems: [
        { text: "Home", to: "/#home", hash: "#home" },
        { text: "Pricing", to: "/#pricing", hash: "#pricing" },
        { text: "Portfolio", to: "/#portfolio", hash: "#portfolio" },
        { text: "About", to: "/#about", hash: "#about" },
        { text: "Contact", to: "/#contact", hash: "#contact" },
      ],
    };
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      if (this.isMenuOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
    closeMenu() {
      this.isMenuOpen = false;
      document.body.style.overflow = "";
    },
    isActiveLink(item) {
      if (this.$route.path === "/" && this.$route.hash === item.hash) {
        return true;
      }
      if (
        item.text === "Home" &&
        this.$route.path === "/" &&
        !this.$route.hash
      ) {
        return true;
      }
      return false;
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);

    window.addEventListener("resize", () => {
      if (window.innerWidth > 968 && this.isMenuOpen) {
        this.closeMenu();
      }
    });

    this.$router.afterEach((to) => {
      if (to.hash) {
        this.$nextTick(() => {
          setTimeout(() => {
            const element = document.querySelector(to.hash);
            if (element) {
              element.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        });
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: all 0.3s ease;
  background: transparent;
  padding: 10px;
}

.header-scrolled {
  background: rgba(0, 0, 0, 0.9) !important;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  padding: 0px;
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
}

.logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 8px;
  z-index: 1001;
}

.logo-text {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  transition: opacity 0.3s ease;
}

.logo:hover .logo-text {
  opacity: 0.8;
}

.logo-dot {
  width: 6px;
  height: 6px;
  background: #4f7df3;
  border-radius: 50%;
  margin-left: 2px;
}

.desktop-nav {
  margin: 0 auto;
}

.nav-list {
  display: flex;
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  position: relative;
}

.nav-link {
  position: relative;
  font-size: 16px;
  font-weight: 400;
  color: #ffffff;
  text-decoration: none;
  padding: 8px 0;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #4f7df3;
}

.nav-indicator {
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: #4f7df3;
  transition: width 0.3s ease;
}

.nav-link:hover .nav-indicator,
.nav-link.active .nav-indicator {
  width: 100%;
}

.desktop-cta .cta-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #4f7df3;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.desktop-cta .cta-button:hover {
  background: #3d66d9;
  transform: translateY(-1px);
}

.cta-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.3s ease;
}

.desktop-cta .cta-button:hover .cta-icon {
  transform: translateX(3px);
}

.mobile-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1001;
  gap: 4px;
}

.hamburger-line {
  width: 20px;
  height: 2px;
  background: #ffffff;
  transition: all 0.3s ease;
  transform-origin: center;
}

.mobile-toggle.active .hamburger-line:nth-child(1) {
  transform: translateY(6px) rotate(45deg);
}

.mobile-toggle.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.mobile-toggle.active .hamburger-line:nth-child(3) {
  transform: translateY(-6px) rotate(-45deg);
}

.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 999;
}

.mobile-overlay.active {
  opacity: 1;
  visibility: visible;
}

.mobile-nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: #000000;
  transform: translateY(-100%);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 1000;
}

.mobile-nav.active {
  transform: translateY(0);
}

.mobile-nav-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
}

.mobile-nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: center;
}

.mobile-nav-item {
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.mobile-nav.active .mobile-nav-item {
  opacity: 1;
  transform: translateY(0);
  transition-delay: var(--delay);
}

.mobile-nav-link {
  display: block;
  text-decoration: none;
  color: #ffffff;
  font-size: 32px;
  font-weight: 400;
  padding: 10px 0;
  transition: all 0.3s ease;
  position: relative;
}

.mobile-nav-link::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background: #4f7df3;
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.mobile-nav-link:hover,
.mobile-nav-link.active {
  color: #4f7df3;
}

.mobile-nav-link:hover::after,
.mobile-nav-link.active::after {
  width: 40px;
}

.mobile-cta {
  margin-top: 3rem;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.mobile-nav.active .mobile-cta {
  opacity: 1;
  transform: translateY(0);
  transition-delay: var(--delay);
}

.mobile-cta-button {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: #4f7df3;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.mobile-cta-button:hover {
  background: #3d66d9;
  transform: translateY(-2px);
}

.mobile-cta-icon {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.mobile-cta-button:hover .mobile-cta-icon {
  transform: translateX(4px);
}

@media (max-width: 968px) {
  .desktop-nav,
  .desktop-cta {
    display: none;
  }

  .mobile-toggle {
    display: flex;
  }

  .header-container {
    padding: 0 1.5rem;
  }
}

@media (max-width: 480px) {
  .mobile-nav-link {
    font-size: 28px;
  }

  .mobile-cta-button {
    font-size: 16px;
    padding: 14px 28px;
  }
}

* {
  -webkit-tap-highlight-color: transparent;
}
</style>
